// settings-reconcile.ts — the settings ↔ keyed-list adapter.
//
// The ONLY place a settings row's DOM is built. The host produces a flat list of
// `RowDescriptor`s (pure data, open-state already baked in); this module turns
// each into a `Cell` for the generic keyed reconciler (ui/keyed-list.ts):
//   - `mountRow(desc)`  builds the <li> ONCE (markup byte-identical to the old
//                       host `_row`/`_rawRow`),
//   - `patchRow(el,desc)` updates a retained <li> in place — class / dataset /
//                       aria / label / value / activation intent — without
//                       rebuilding the node.
//
// A control (`control`) or raw row body (`content`) is a `() => Node` FACTORY:
// built once on mount and RETAINED across patches; push new state into the
// retained node via `updateControl`/`update`. Because a row's body is never
// rebuilt, an input's focus/cursor, an open native colour picker, and live drag
// handlers all survive the re-renders a section fires. State that must structurally
// change the body is encoded in the row's reconcile `key`, so it remounts cleanly.

import { buildSquiggleSVG } from '../utils/squiggle.js';
import type { Cell } from '../ui/keyed-list.js';
import type {
  RawRowDescriptor,
  RowDescriptor,
  TemplatedRowDescriptor,
} from './settings-section-api.js';

/** A row descriptor's indent as a numeric depth: `true`/`1` → 1, `n` → n, else 0. */
export function indentDepth(indent: number | boolean | undefined): number {
  return indent === true ? 1 : typeof indent === 'number' ? indent : 0;
}

/** Map descriptors → keyed-list cells; drop (and warn on) a duplicate key. */
export function descsToCells(descs: RowDescriptor[]): Cell[] {
  const seen = new Set<string>();
  const cells: Cell[] = [];
  for (const desc of descs) {
    if (seen.has(desc.key)) {
      console.warn(`[settings] duplicate row key "${desc.key}" — later row dropped`);
      continue;
    }
    seen.add(desc.key);
    cells.push({
      key: desc.key,
      mount: () => mountRow(desc),
      update: (el) => patchRow(el, desc),
    });
  }
  return cells;
}

/** The desired `class` string for a row — used by both mount and patch so they
 * can never drift. */
function rowClass(desc: RowDescriptor): string {
  const c: string[] = ['settings-row'];
  if (desc.kind === 'raw') c.push('settings-row-content');
  if (desc.cls) for (const x of desc.cls.split(/\s+/)) if (x) c.push(x);
  if (desc.kind === 'row' && desc.active) c.push('settings-row-active');
  if (desc.indent) c.push('settings-row-indent');
  if (desc.indent === 2) c.push('settings-row-indent-2');
  if (desc.indent === 3) c.push('settings-row-indent-3');
  if (desc.kind === 'row') {
    if (desc.expandable) {
      c.push('settings-row-expandable');
      if (desc.open) c.push('open');
    } else if (desc.onActivate) {
      c.push('settings-row-clickable');
    }
  }
  return c.join(' ');
}

/** Build a row's <li> from scratch. Called once per key by the reconciler. */
function mountRow(desc: RowDescriptor): HTMLElement {
  const li = document.createElement('li');
  li.className = rowClass(desc);
  li.dataset.search = desc.searchText;
  li.dataset.section = String(desc.section);
  if (desc.kind === 'raw') mountRaw(li, desc);
  else mountTemplated(li, desc);
  paintRow(li, desc);
  return li;
}

/** The CSS colour expression for a fold section's line — `var(--accent-line-
 * sec-<idx>, faint)`, matching the editor so the per-section hover var brightens
 * exactly this line. */
function lineColor(idx: number): string {
  return `var(--accent-line-sec-${idx},var(--accent-line-faint))`;
}

// Paint a row's vertical accent lines inline from its stamped `lineSectionIds`,
// one 2px gradient per indent slot (slot i at x = 8 + i*20), each in its owning
// section's colour. Also mirrors the fold index to `data-line-sec` /
// `data-fold-sec` so the host's hover + click paths can read which section a
// given slot belongs to. The lines are inline (not a CSS class) precisely so
// each slot can carry its OWN section colour — the prerequisite for editor-style
// per-section hover brightening. Indices are stable per group, so a patch
// re-applies the identical style (no fl/icker).
function paintRow(li: HTMLElement, desc: RowDescriptor): void {
  const ids = desc.lineSectionIds;
  if (ids?.length) {
    // Group fold-indices are stable per id, so an unchanged ancestry re-paints
    // byte-identical gradients. Guard the CSSOM work (string builds + 5 style
    // writes) on the signature — matching the guarded-write discipline the rest
    // of patchRow already follows. Colours still track the theme through the
    // `var(--accent-line-sec-*)` CSS vars, so skipping never staleifies them.
    const sig = ids.join(',');
    if (li.dataset.lineSec !== sig) {
      const imgs: string[] = [];
      const poss: string[] = [];
      const sizes: string[] = [];
      for (let i = 0; i < ids.length; i++) {
        const c = lineColor(ids[i]);
        imgs.push(`linear-gradient(${c},${c})`);
        poss.push(`${8 + i * 20}px 0`);
        sizes.push('2px 100%');
      }
      li.style.backgroundImage = imgs.join(',');
      li.style.backgroundPosition = poss.join(',');
      li.style.backgroundSize = sizes.join(',');
      li.style.backgroundRepeat = 'no-repeat';
      li.style.backgroundOrigin = 'border-box';
      li.dataset.lineSec = sig;
    }
  } else {
    if (li.style.backgroundImage) {
      li.style.backgroundImage = '';
      li.style.backgroundPosition = '';
      li.style.backgroundSize = '';
    }
    if (li.dataset.lineSec != null) delete li.dataset.lineSec;
  }
  const foldIdx = desc.kind === 'row' ? desc.foldSectionIdx : undefined;
  if (foldIdx != null) li.dataset.foldSec = String(foldIdx);
  else if (li.dataset.foldSec != null) delete li.dataset.foldSec;
}

function mountTemplated(li: HTMLElement, desc: TemplatedRowDescriptor): HTMLElement {
  if (desc.id) li.dataset.rowId = desc.id;

  const left = document.createElement('span');
  left.className = 'settings-row-label';
  const txt = document.createElement('span');
  txt.className = 'settings-row-text';
  txt.textContent = desc.label;
  left.appendChild(txt);
  li.appendChild(left);
  // Stash the mutable spans on the node so patchRow updates them without
  // re-running querySelector on every patch.
  (li as any)._textEl = txt;
  (li as any)._lastLabel = desc.label;
  wireEditableLabel(li, txt);
  syncEditable(li, txt, desc);

  if (desc.control != null) {
    const right = document.createElement('span');
    right.className = 'settings-row-control';
    const node = desc.control();
    right.appendChild(node);
    li.appendChild(right);
    (li as any)._controlEl = node;
  } else if (desc.value != null) {
    const right = document.createElement('span');
    right.className = 'settings-row-value';
    right.textContent = String(desc.value);
    li.appendChild(right);
    (li as any)._valueEl = right;
  }

  syncAux(li, desc);
  syncSquiggle(li, desc);

  if (desc.expandable) {
    li.tabIndex = 0;
    li.setAttribute('aria-expanded', String(!!desc.open));
  } else if (desc.onActivate) {
    li.tabIndex = 0;
  }
  (li as any)._onActivate = desc.onActivate;
  (li as any)._onAux = desc.onAux;
  return li;
}

// Inline-editable label: wire the commit/revert listeners ONCE per row (they
// read the live `_onEditCommit` / `_lastLabel` off the node, refreshed each
// patch, so the closure never goes stale). Enter commits + blurs; Escape
// reverts to the last label; blur commits the trimmed value (unless it was an
// Escape). `syncEditable` toggles whether the span is actually contenteditable.
function wireEditableLabel(li: HTMLElement, txt: HTMLElement): void {
  if ((li as any)._editWired) return;
  (li as any)._editWired = true;
  const isEditable = () => txt.getAttribute('contenteditable') != null;
  txt.addEventListener('keydown', (e: KeyboardEvent) => {
    if (!isEditable()) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      txt.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      (li as any)._editEscape = true;
      txt.blur();
    }
  });
  txt.addEventListener('blur', () => {
    if (!isEditable()) return;
    if ((li as any)._editEscape) {
      (li as any)._editEscape = false;
      txt.textContent = (li as any)._lastLabel ?? '';
      return;
    }
    const commit = (li as any)._onEditCommit as ((v: string) => void) | undefined;
    commit?.((txt.textContent || '').trim());
  });
}

// Reflect the descriptor's `editable` onto the span: make it contenteditable
// (or revert to static), keep the live commit callback fresh, and set the a11y
// role/label. The EXISTENCE of editing follows the descriptor — the active
// workspace flipping active↔inactive turns its name field on/off on patch.
function syncEditable(li: HTMLElement, txt: HTMLElement, desc: TemplatedRowDescriptor): void {
  const editable = desc.editable;
  (li as any)._onEditCommit = editable?.onCommit;
  if (editable) {
    if (txt.getAttribute('contenteditable') == null) {
      // plaintext-only avoids pasted rich markup; falls back to "true" where
      // unsupported (the keydown handler strips newlines either way).
      txt.setAttribute('contenteditable', 'plaintext-only');
      if (!txt.isContentEditable) txt.setAttribute('contenteditable', 'true');
      txt.spellcheck = false;
      txt.setAttribute('role', 'textbox');
    }
    setAttr(txt, 'aria-label', editable.ariaLabel || null);
  } else if (txt.getAttribute('contenteditable') != null) {
    txt.removeAttribute('contenteditable');
    txt.removeAttribute('role');
    txt.removeAttribute('aria-label');
  }
}

// Dual-action rows: a trailing "⋯" button sub-target (body activates, aux
// toggles the secondary surface, e.g. a workspace's manage panel). Unlike the
// label/control, the aux element's EXISTENCE follows the descriptor — a
// retained row gains/loses it on patch when onAux appears/disappears.
function syncAux(li: HTMLElement, desc: TemplatedRowDescriptor): void {
  let aux = (li as any)._auxEl as HTMLElement | undefined;
  if (desc.onAux && !aux) {
    aux = document.createElement('span');
    aux.className = 'settings-row-aux';
    aux.textContent = '⋯';
    aux.setAttribute('role', 'button');
    aux.tabIndex = -1;
    li.appendChild(aux);
    (li as any)._auxEl = aux;
  } else if (!desc.onAux && aux) {
    aux.remove();
    (li as any)._auxEl = undefined;
    aux = undefined;
  }
  if (aux && desc.auxLabel) setAttr(aux, 'aria-label', desc.auxLabel);
}

// Collapsed-group fold stub — the editor's fold-squiggle, ported. A collapsed
// expandable row shows a short squiggle below its text at the slot where its
// children's vertical accent line would run if it were open — exactly how the
// editor marks a folded section ("a shortened version of the vertical line the
// section would have if expanded"). Removed once the row opens (the real lines
// take over) or if the row stops being expandable. It IS the click target that
// expands the group, and its colour is set inline to its own section's line var
// (see below) so hovering it brightens exactly that section, like the editor.
//
// Skipped when the row carries an `onAux` "⋯" button (workspace rows): that
// button is already the row's expand affordance, so a squiggle would just add
// clutter + height to the list. The stub is for expandable rows that would
// otherwise show NO collapsed affordance (the section groups, Members,
// Devices, calendar accounts, Event syntax …).
function syncSquiggle(li: HTMLElement, desc: TemplatedRowDescriptor): void {
  const want = !!desc.expandable && !desc.open && !desc.onAux;
  let sq = (li as any)._sqEl as HTMLElement | undefined;
  if (want && !sq) {
    sq = document.createElement('span');
    sq.className = 'settings-fold-squiggle';
    sq.setAttribute('aria-hidden', 'true');
    sq.appendChild(
      buildSquiggleSVG({ width: 16, height: 15, pad: 2, strokeWidth: 1.5, period: 6, rx: 3.5 })
    );
    li.appendChild(sq);
    (li as any)._sqEl = sq;
  } else if (!want && sq) {
    sq.remove();
    (li as any)._sqEl = undefined;
    sq = undefined;
  }
  if (sq) {
    // Offset to the row's own child slot: each indent level is a 20px column and
    // the first child line sits 8px in, so a level-L row's children line at
    // x = 8 + L*20. The 16px-wide SVG centres its path at x=8, so left = L*20
    // lands that centre exactly on the child line.
    const lvl = indentDepth(desc.indent);
    sq.style.left = `${lvl * 20}px`;
    // Colour it with its own section's line var so hovering the squiggle
    // brightens exactly this section (and nothing else), like the editor.
    sq.style.color =
      desc.foldSectionIdx != null ? lineColor(desc.foldSectionIdx) : 'var(--accent-line-faint)';
  }
}

function mountRaw(li: HTMLElement, desc: RawRowDescriptor): HTMLElement {
  if (desc.content != null) {
    const node = desc.content();
    li.appendChild(node);
    (li as any)._contentEl = node;
  }
  return li;
}

/** Patch a retained <li> to match `desc`, never replacing the node itself. */
function patchRow(li: HTMLElement, desc: RowDescriptor): void {
  const cls = rowClass(desc);
  if (li.className !== cls) li.className = cls;
  if (li.dataset.search !== desc.searchText) li.dataset.search = desc.searchText;
  const sec = String(desc.section);
  if (li.dataset.section !== sec) li.dataset.section = sec;
  paintRow(li, desc);
  // Refresh activation intents so the delegated listener dispatches the latest
  // handler (the retained node's listener is on the <ul>, not the row).
  (li as any)._onActivate = desc.kind === 'row' ? desc.onActivate : undefined;
  (li as any)._onAux = desc.kind === 'row' ? desc.onAux : undefined;

  if (desc.kind === 'raw') {
    patchRawContent(li, desc);
    return;
  }

  // Keep the aux sub-target in sync — its existence follows desc.onAux, and a
  // retained aux's aria-label stays fresh (e.g. workspaces switching active
  // flips "Expand X" ↔ "Manage X" on a retained row).
  syncAux(li, desc);
  // Add/remove the collapsed-group fold squiggle as the row's open-state flips.
  syncSquiggle(li, desc);

  if (desc.id) {
    if (li.dataset.rowId !== desc.id) li.dataset.rowId = desc.id;
  } else if (li.dataset.rowId != null) {
    delete li.dataset.rowId;
  }
  setAttr(li, 'aria-expanded', desc.expandable ? String(!!desc.open) : null);
  setAttr(li, 'tabindex', desc.expandable || desc.onActivate ? '0' : null);

  const txt = (li as any)._textEl as HTMLElement | undefined;
  // Don't clobber the label while the user is editing it (focused
  // contenteditable) — a re-render fired by an unrelated change would otherwise
  // wipe the in-progress name. Refresh the editable wiring + remember the label
  // so an Escape can revert to it.
  if (txt) {
    syncEditable(li, txt, desc);
    // An editable label that's focused is mid-edit — leave its text alone so a
    // re-render fired by an unrelated change can't wipe what the user typed.
    const editing = txt.getAttribute('contenteditable') != null && document.activeElement === txt;
    if (!editing && txt.textContent !== desc.label) txt.textContent = desc.label;
    (li as any)._lastLabel = desc.label;
  }

  if (desc.control != null) {
    // Retained factory control — push any new state in; never rebuilt.
    const el = (li as any)._controlEl as HTMLElement | undefined;
    if (el && desc.updateControl) desc.updateControl(el);
  } else if (desc.value != null) {
    const v = (li as any)._valueEl as HTMLElement | undefined;
    if (v && v.textContent !== String(desc.value)) v.textContent = String(desc.value);
  }
}

function patchRawContent(li: HTMLElement, desc: RawRowDescriptor): void {
  // Retained factory content — push any new state in; never rebuilt.
  const cur = (li as any)._contentEl as HTMLElement | undefined;
  if (cur && desc.update) desc.update(cur);
}

function setAttr(li: HTMLElement, name: string, value: string | null): void {
  if (value == null) {
    if (li.hasAttribute(name)) li.removeAttribute(name);
  } else if (li.getAttribute(name) !== value) {
    li.setAttribute(name, value);
  }
}
